import type { Wall, Point, WallType } from '../types';
import { pointsAreEqual, distanceToLine, generateStableWallId } from './floorPlanUtils';

// Constants for segmentation
const INTERSECTION_TOLERANCE = 0.5; // Pixels
const ENDPOINT_TOLERANCE = 2.0; // Pixels for endpoint matching

export interface WallSegment {
  id: string;                    // Unique identifier for the segment
  parentWallId: string;          // ID of the original wall this segment belongs to
  start: Point;                  // Start point of the segment
  end: Point;                    // End point of the segment
  type: WallType;                // Wall type for this specific segment
  floor: number;                 // Floor number
  segmentIndex: number;          // Index within the parent wall (0, 1, 2, etc.)
  isMainSegment: boolean;        // True if this is the largest/main segment of the wall
  intersectionPoints: Point[];   // Points where this segment intersects with other walls
  length: number;                // Length of the segment in pixels
  parentWallStart: Point;        // Original start point of the parent wall
  parentWallEnd: Point;          // Original end point of the parent wall
}

export interface SegmentationResult {
  segments: WallSegment[];
  originalWalls: Wall[];
  intersectionMap: Map<string, Point[]>; // Map of wall IDs to their intersection points
}

/**
 * Analyzes walls and creates segments based on intersection points
 */
export function analyzeWallSegmentation(walls: Wall[]): SegmentationResult {
  const intersectionMap = new Map<string, Point[]>();
  const segments: WallSegment[] = [];
  
  console.log('🔧 WALL SEGMENTATION: Starting analysis', {
    wallCount: walls.length,
    walls: walls.map(w => ({ start: w.start, end: w.end, type: w.type }))
  });

  // Step 1: Find all intersection points for each wall
  walls.forEach((wall, wallIndex) => {
    const wallId = getWallId(wall, wallIndex);
    const intersections: Point[] = [];
    
    // Check intersections with all other walls
    walls.forEach((otherWall, otherIndex) => {
      if (wallIndex === otherIndex) return;
      
      const intersection = findWallIntersection(wall, otherWall);
      if (intersection && isIntersectionValid(intersection, wall, otherWall)) {
        // Check if this intersection is already recorded
        const isDuplicate = intersections.some(existing => 
          pointsAreEqual(existing, intersection, ENDPOINT_TOLERANCE)
        );
        
        if (!isDuplicate) {
          intersections.push(intersection);
          console.log('🔧 INTERSECTION FOUND', {
            wall1: `${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)}->${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)}`,
            wall2: `${otherWall.start.x.toFixed(1)},${otherWall.start.y.toFixed(1)}->${otherWall.end.x.toFixed(1)},${otherWall.end.y.toFixed(1)}`,
            intersection: `${intersection.x.toFixed(1)},${intersection.y.toFixed(1)}`
          });
        }
      }
    });
    
    intersectionMap.set(wallId, intersections);
  });

  // Step 2: Create segments for each wall
  walls.forEach((wall, wallIndex) => {
    const wallId = getWallId(wall, wallIndex);
    const intersections = intersectionMap.get(wallId) || [];
    
    if (intersections.length === 0) {
      // No intersections - wall remains as single segment
      segments.push({
        id: `${wallId}_seg_0`,
        parentWallId: wallId,
        start: wall.start,
        end: wall.end,
        type: wall.type,
        floor: wall.floor,
        segmentIndex: 0,
        isMainSegment: true,
        intersectionPoints: [],
        length: calculateDistance(wall.start, wall.end),
        parentWallStart: wall.start,
        parentWallEnd: wall.end
      });
    } else {
      // Create segments between intersection points
      const wallSegments = createWallSegments(wall, intersections, wallId);
      segments.push(...wallSegments);
    }
  });

  console.log('🔧 WALL SEGMENTATION: Analysis complete', {
    originalWalls: walls.length,
    totalSegments: segments.length,
    wallsWithIntersections: Array.from(intersectionMap.entries())
      .filter(([_, intersections]) => intersections.length > 0).length
  });

  return {
    segments,
    originalWalls: walls,
    intersectionMap
  };
}

/**
 * Finds intersection point between two walls
 */
function findWallIntersection(wall1: Wall, wall2: Wall): Point | null {
  const x1 = wall1.start.x, y1 = wall1.start.y;
  const x2 = wall1.end.x, y2 = wall1.end.y;
  const x3 = wall2.start.x, y3 = wall2.start.y;
  const x4 = wall2.end.x, y4 = wall2.end.y;
  
  const denominator = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  
  if (Math.abs(denominator) < 1e-10) {
    // Lines are parallel - check for collinear overlap
    return findCollinearIntersection(wall1, wall2);
  }
  
  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denominator;
  const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denominator;
  
  // Check if intersection is within both line segments
  if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
    return {
      x: x1 + t * (x2 - x1),
      y: y1 + t * (y2 - y1)
    };
  }
  
  return null;
}

/**
 * Handle collinear wall overlap to find segmentation points
 */
function findCollinearIntersection(wall1: Wall, wall2: Wall): Point | null {
  // Check if walls are collinear
  const distance1 = distanceToLine(wall2.start, wall1.start, wall1.end);
  const distance2 = distanceToLine(wall2.end, wall1.start, wall1.end);
  
  if (distance1 > INTERSECTION_TOLERANCE || distance2 > INTERSECTION_TOLERANCE) {
    return null; // Not collinear
  }
  
  // Find overlap points for collinear walls
  const wall1Vector = { x: wall1.end.x - wall1.start.x, y: wall1.end.y - wall1.start.y };
  const wall1Length = Math.sqrt(wall1Vector.x * wall1Vector.x + wall1Vector.y * wall1Vector.y);
  
  if (wall1Length === 0) return null;
  
  // Project wall2 endpoints onto wall1
  const projections = [wall2.start, wall2.end].map(point => {
    const toPoint = { x: point.x - wall1.start.x, y: point.y - wall1.start.y };
    const projection = (toPoint.x * wall1Vector.x + toPoint.y * wall1Vector.y) / (wall1Length * wall1Length);
    return {
      point,
      projection: projection * wall1Length,
      t: projection
    };
  });
  
  // Find overlapping region
  const validProjections = projections.filter(p => p.t >= 0 && p.t <= 1);
  
  if (validProjections.length > 0) {
    // Return the first valid intersection point for segmentation
    return validProjections[0].point;
  }
  
  return null;
}

/**
 * Validates if an intersection is meaningful for segmentation
 */
function isIntersectionValid(intersection: Point, wall1: Wall, wall2: Wall): boolean {
  // Intersection shouldn't be at the endpoints unless it's a T-junction
  const isWall1Endpoint = pointsAreEqual(intersection, wall1.start, ENDPOINT_TOLERANCE) ||
                          pointsAreEqual(intersection, wall1.end, ENDPOINT_TOLERANCE);
  const isWall2Endpoint = pointsAreEqual(intersection, wall2.start, ENDPOINT_TOLERANCE) ||
                          pointsAreEqual(intersection, wall2.end, ENDPOINT_TOLERANCE);
  
  // Allow T-junctions (one endpoint, one midpoint)
  if (isWall1Endpoint && isWall2Endpoint) {
    return false; // Both endpoints - likely a corner, not a segmentation point
  }
  
  // Valid if it's a T-junction or a true intersection
  return true;
}

/**
 * Creates wall segments between intersection points
 */
function createWallSegments(wall: Wall, intersections: Point[], wallId: string): WallSegment[] {
  const segments: WallSegment[] = [];
  
  // Sort intersection points along the wall direction
  const wallVector = { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y };
  const wallLength = Math.sqrt(wallVector.x * wallVector.x + wallVector.y * wallVector.y);
  
  if (wallLength === 0) {
    // Degenerate wall
    return [];
  }
  
  const sortedIntersections = intersections
    .map(point => {
      const toPoint = { x: point.x - wall.start.x, y: point.y - wall.start.y };
      const projection = (toPoint.x * wallVector.x + toPoint.y * wallVector.y) / (wallLength * wallLength);
      return {
        point,
        distance: projection * wallLength,
        t: projection
      };
    })
    .filter(item => item.t > 0.01 && item.t < 0.99) // Exclude very close to endpoints
    .sort((a, b) => a.distance - b.distance);

  // Create segments between points
  const allPoints = [
    { point: wall.start, distance: 0, t: 0 },
    ...sortedIntersections,
    { point: wall.end, distance: wallLength, t: 1 }
  ];

  for (let i = 0; i < allPoints.length - 1; i++) {
    const startPoint = allPoints[i];
    const endPoint = allPoints[i + 1];
    const segmentLength = endPoint.distance - startPoint.distance;
    
    // Only create segments that are meaningful in size
    if (segmentLength > 5) { // Minimum 5 pixels
      const isMainSegment = i === 0 || segmentLength === Math.max(
        ...allPoints.slice(0, -1).map((_, idx) => 
          allPoints[idx + 1].distance - allPoints[idx].distance
        )
      );
      
      segments.push({
        id: `${wallId}_seg_${i}`,
        parentWallId: wallId,
        start: startPoint.point,
        end: endPoint.point,
        type: wall.type,
        floor: wall.floor,
        segmentIndex: i,
        isMainSegment,
        intersectionPoints: sortedIntersections.map(item => item.point),
        length: segmentLength,
        parentWallStart: wall.start,
        parentWallEnd: wall.end
      });
    }
  }

  console.log('🔧 CREATED SEGMENTS', {
    wallId,
    originalWall: `${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)}->${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)}`,
    intersectionCount: intersections.length,
    segmentCount: segments.length,
    segments: segments.map(s => ({
      id: s.id,
      start: `${s.start.x.toFixed(1)},${s.start.y.toFixed(1)}`,
      end: `${s.end.x.toFixed(1)},${s.end.y.toFixed(1)}`,
      length: s.length.toFixed(1),
      isMain: s.isMainSegment
    }))
  });

  return segments;
}

/**
 * Finds the specific segment that contains a given point
 */
export function findSegmentAtPoint(point: Point, segments: WallSegment[], tolerance: number = 10): WallSegment | null {
  let closestSegment: WallSegment | null = null;
  let closestDistance = tolerance;
  
  for (const segment of segments) {
    const distance = distanceToLine(point, segment.start, segment.end);
    
    if (distance < closestDistance && isPointOnSegment(point, segment, tolerance)) {
      closestDistance = distance;
      closestSegment = segment;
    }
  }
  
  return closestSegment;
}

/**
 * Checks if a point is on a specific segment
 */
function isPointOnSegment(point: Point, segment: WallSegment, tolerance: number): boolean {
  const distance = distanceToLine(point, segment.start, segment.end);
  if (distance > tolerance) return false;
  
  // Check if point is within segment bounds
  const segmentVector = { x: segment.end.x - segment.start.x, y: segment.end.y - segment.start.y };
  const segmentLength = Math.sqrt(segmentVector.x * segmentVector.x + segmentVector.y * segmentVector.y);
  
  if (segmentLength === 0) return false;
  
  const toPoint = { x: point.x - segment.start.x, y: point.y - segment.start.y };
  const projection = (toPoint.x * segmentVector.x + toPoint.y * segmentVector.y) / (segmentLength * segmentLength);
  
  return projection >= -0.1 && projection <= 1.1; // Small tolerance for endpoints
}

/**
 * Updates a specific segment's wall type
 */
export function updateSegmentType(segments: WallSegment[], segmentId: string, newType: WallType): WallSegment[] {
  return segments.map(segment => 
    segment.id === segmentId 
      ? { ...segment, type: newType }
      : segment
  );
}

/**
 * Converts segments back to walls for compatibility with existing system
 * Intelligently reconstructs walls by merging segments of the same type
 */
export function segmentsToWalls(segments: WallSegment[]): Wall[] {
  const segmentsByParent = new Map<string, WallSegment[]>();
  
  // Group segments by parent wall
  segments.forEach(segment => {
    if (!segmentsByParent.has(segment.parentWallId)) {
      segmentsByParent.set(segment.parentWallId, []);
    }
    segmentsByParent.get(segment.parentWallId)!.push(segment);
  });

  const reconstructedWalls: Wall[] = [];

  segmentsByParent.forEach((parentSegments, parentWallId) => {
    // Sort segments by index to maintain order
    parentSegments.sort((a, b) => a.segmentIndex - b.segmentIndex);
    
    // Group consecutive segments with the same type
    const typeGroups: WallSegment[][] = [];
    let currentGroup: WallSegment[] = [];
    let currentType: WallType | null = null;

    parentSegments.forEach(segment => {
      if (segment.type !== currentType) {
        if (currentGroup.length > 0) {
          typeGroups.push(currentGroup);
        }
        currentGroup = [segment];
        currentType = segment.type;
      } else {
        currentGroup.push(segment);
      }
    });
    
    if (currentGroup.length > 0) {
      typeGroups.push(currentGroup);
    }

    // Create walls for each type group
    typeGroups.forEach((group, groupIndex) => {
      const firstSegment = group[0];
      const lastSegment = group[group.length - 1];
      
      // Create wall spanning from first segment start to last segment end
      const reconstructedWall: Wall = {
        start: firstSegment.start,
        end: lastSegment.end,
        type: firstSegment.type,
        floor: firstSegment.floor,
        // Generate consistent ID for the reconstructed wall
        id: group.length === 1 && firstSegment.isMainSegment 
          ? parentWallId // Keep original ID if it's the main unchanged segment
          : `${parentWallId}_seg_${groupIndex}`, // New ID for split walls
      };

      reconstructedWalls.push(reconstructedWall);
    });
  });

  console.log('🔧 SEGMENTS TO WALLS CONVERSION', {
    originalSegments: segments.length,
    reconstructedWalls: reconstructedWalls.length,
    parentWalls: segmentsByParent.size
  });

  return reconstructedWalls;
}

/**
 * Gets all segments that belong to a specific parent wall
 */
export function getWallSegments(segments: WallSegment[], parentWallId: string): WallSegment[] {
  return segments.filter(segment => segment.parentWallId === parentWallId);
}

/**
 * Helper functions
 */
function getWallId(wall: Wall, _index?: number): string {
  // 🔧 FIX: Always use generateStableWallId for consistency
  // This prevents room creation order dependency issues where tempId differs
  // between room creation and segment lookup operations
  // Note: index parameter is no longer used but kept for backward compatibility
  return generateStableWallId(wall.start, wall.end);
  
  // REMOVED: Inconsistent ID generation that caused room order dependency
  // if ('id' in wall && wall.id) return wall.id;
  // if ('tempId' in wall && (wall as any).tempId) return (wall as any).tempId;
  // return generateStableWallId(wall.start, wall.end);
}

function calculateDistance(p1: Point, p2: Point): number {
  return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
} 